import {
  FluidAlignment,
  FluidDataLayerComponentConfiguration,
  FluidFieldValidation,
  FluidFieldValidationType,
  FluidFormElementConfig,
  FluidFormElementOutputType,
  FluidFormElementState,
  FluidFormState,
  FluidIconConfig,
  FluidInputMaskingConfig,
  FluidInputType,
  FluidLanguage,
  FluidPopoverConfig,
  FluidPrimitive,
  FluidRoundingMethod,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidUpdateStrategy,
  HTMLFluidInputFieldElement,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidFormElementTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-form-element-translation-options.interface';
import { FluidFormatters } from '@lmig/fluid-core/lib/utils/formatting.utils';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  FormElementContainer,
  FormElementWrapper,
} from '../../common/fluid-common-form.components';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../utils/events/event-utils';
import {
  emitValueChangeEvent,
  getFormInputId,
  getValue,
  handleAutoFocus,
  handleNumberCoercion,
  hasValue,
  isDebouncedDelete,
  markTouchedAndValidate,
  reset,
  setValue,
  shouldApplyInlineFormatting,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import { setInitialElementState } from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';
import { FluidDataLayerUtils } from '../fluid-data-layer/fluid-data-layer.utils';

// TODO: ADD @contextData

/**
 * @displayName Input Field
 *
 * @advancedUsage /forms
 *
 * @builder true
 */
@Component({
  tag: 'fluid-input-field',
  styleUrl: 'fluid-input-field.scss',
  shadow: true,
})
export class FluidInputField
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to setTimeout for "maskingConfig.blurDelay". Allows
   * timeout to be cancelled if Input Field receives :focus before
   * callback is executed.
   */
  private _maskingDelay;

  /**
   * Boolean that indicates whether "required" validation exists in the
   * "validation" Prop array. Controls whether "required" attribute is
   * added to core <input> element.
   */
  private _isRequired = false;

  /**
   * Boolean that tracks whether the form field currently has :focus.
   */
  private _hasFocus = false;

  // ================================================================ //
  // -- Validation Variables

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <input> element.
   */
  _elementRef: HTMLInputElement;

  /**
   * Boolean that tracks whether the form field has received autofocus.
   */
  _autoFocus = false;

  _debouncedDelete: Function;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidInputFieldElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  /**
   * Boolean that controls whether masking is currently applied to
   * the form field's value.
   */
  @State() _maskingActive = false;

  /**
   * Boolean that controls whether <input> value is dynamically updated
   * to mirror current "_elementState.value". Used to delay "wholeOnly"
   * rounding from being displayed until the component loses :focus.
   */
  @State() _updateDisplayValue = true;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element).
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * The form field's <input> "type" that dictates the "value" data format.
   */
  @Prop({ mutable: true }) type: FluidInputType = FluidInputType.TEXT;

  /**
   * Default value to be displayed within Input Field's <input> on first
   * load. NOTE: this Prop is NOT dynamically updated after initial load
   * like the "value" Prop is, & cannot be used to update the form field's
   * value after the first render.
   */
  @Prop({ mutable: true }) initialValue: FluidPrimitive;

  /**
   * The Input Field's current value. This Prop is dynamically updated to
   * reflect the form field's current state, so it can be used as a
   * "synchronous" alternative to the public async "getValue()" method.
   * If provided on first load, populates the form field with a default
   * value. After the component is rendered, changes to this Prop will
   * update the form field's value (similar to "setValue()" method). NOTE:
   * "initialValue" will override this Prop on first load if both Props
   * are provided.
   */
  @Prop({ mutable: true }) value: FluidPrimitive;

  @Watch('value')
  onValueChanged() {
    if (this.valueEqualsState()) return;

    this.touched = false;
    this._elementState = {
      ...this._elementState,
      value: this.parseValue(this.value as string),
      touched: this.touched,
    };

    this.validate().then(() => this.applyRounding());
    this.applyMasking(true);
  }

  /**
   * Determines the updating strategy for the field;
   * 'input' - the field will update the element state on each change to its value.
   * 'blur' - the field will update the element state when the user leaves the field.
   */
  @Prop({ mutable: true }) updateStrategy: FluidUpdateStrategy =
    FluidUpdateStrategy.INPUT;

  /**
   * Determines whether the output value should remain as a string or be
   * coerced to a number when emitted. NOTE - will only work if `type` is `number`.
   */
  @Prop({ mutable: true }) outputType: FluidFormElementOutputType =
    FluidFormElementOutputType.STRING;

  /**
   * If provided, a FLUID Form Label will be rendered above the Input
   * Field with the specified string.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * If provided, a supplementary muted label will be displayed after the
   * primary Form Label.
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * Optional placeholder text to display within Input Field's <input>.
   */
  @Prop({ mutable: true }) placeholder: string;

  /**
   * If true, Input Field's <input> will be disabled.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * Debounces the delete action by the specified time limit.
   * Useful for preventing functions that act on value change
   * from firing repeatedly when the user deletes characters.
   *
   * @default -1 = If -1, no debounce will occur
   */
  @Prop({ mutable: true }) debounceDelete = -1;

  /**
   * If true, Input Field's <input> will be in a "readonly" state.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * An array of field validation options (see "Validation" & "Custom
   * Validation" tabs within the "Basic Forms Walkthrough" guide in the
   * FLUID Documentation Site for more information).
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  @Watch('validation')
  onValidationChange() {
    this._isRequired = this.isRequired();
  }

  /**
   * If provided, Input Field's Form Label will render a "helptext" icon
   * with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Removes the form field's default bottom margin.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * If true, displays a "clear" button that resets the form field when
   * clicked. The field's <input> must have a value for button to be
   * visible. Button is NOT displayed when field has "required" validation.
   */
  @Prop({ mutable: true }) allowCancel: boolean;

  /**
   * When true, this field will automatically have focus on page load.
   * Note: only one form field should have "autoFocus" set to true
   * on a page. Multiple fields with "autoFocus: true" can/will cause
   * unexpected behavior.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * If provided, applies masking function to the form field's <input>
   * value after the specified time delay.
   */
  @Prop({ mutable: true }) maskingConfig: FluidInputMaskingConfig;

  /**
   * Sets a maximum length (number of characters) allowed to be entered
   * in the form field's <input>. NOTE: does not apply to number fields
   * (i.e. "type" Prop is "number").
   */
  @Prop({ mutable: true }) maxLength: number;

  /**
   * If a number field (i.e. "type" Prop is "number"), allows the "step"
   * increment/decrement to be configured.
   */
  @Prop({ mutable: true }) step = 'any';

  /**
   * If a number field (i.e. "type" Prop is "number"), controls whether
   * "stepper buttons" (i.e. "spin buttons") are displayed on desktop
   * devices.
   */
  @Prop({ mutable: true }) displayStepperButtons = true;

  /**
   * If true and input field is of type number, the <input> will only show
   * whole numbers with zero decimal places. NOTE: the "roundingMethod"
   * Prop determines how the decimal values are rounded.
   */
  @Prop({ mutable: true }) wholeOnly: boolean;

  /**
   * If using the "wholeOnly" Prop, this Prop dictates how decimal values
   * should be rounded to the nearest whole number (e.g. round up or round
   * down). Defaults to always rounding down.
   */
  @Prop({ mutable: true }) roundingMethod: FluidRoundingMethod =
    FluidRoundingMethod.DEFAULT;

  /**
   * Allows Input Field Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidFormElementConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
  }

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when its value changes.
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - set local @Props to "config" values
   * @action: setGlobalTheme - Sets the global theme.
   */
  connectedCallback(): void {
    this._debouncedValueChangeEmitter = Utils.debounce(
      emitValueChangeEvent.bind(this),
      this.debounceDelete
    );
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - sets the internal form element "state" & updates "value" Prop
   */
  componentWillLoad() {
    this.initializeState();
    this._autoFocus = this.autoFocus;
    this._isRequired = this.isRequired();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.applyMasking(true);
  }

  /**
   * Called AFTER every render().
   */
  componentDidRender() {
    handleAutoFocus(this);
  }

  /**
   * Called when component will be updated due to @Prop or @State change.
   * @action: updateValue - dynamically update the "value" Prop to match "_elementState.value"
   */
  componentWillUpdate() {
    this.updateValue();
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    return setValue(this, value, () => this.applyMasking(true), opts);
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    return reset(this, clear);
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Returns true if "value" Prop equals "_elementState.value" OR if
   * "_elementState.value" hasn't been initialized (i.e. is undefined) -
   * otherwise, returns false. Used to determine if "value" Prop is
   * out-of-sync with internal state.
   */
  valueEqualsState(): boolean {
    return (
      isNullOrUndefined(this._elementState?.value) ||
      this.value === this._elementState.value
    );
  }

  /**
   * Dynamically updates the "value" Prop to match "_elementState.value".
   * Called every time the component updates/re-renders.
   */
  updateValue(): void {
    if (this.valueEqualsState()) return;
    this.value = this._elementState.value;
  }

  /**
   * Determines Input Field's default value (if provided), sets up the
   * form field's internal state, & triggers update to "value" Prop.
   */
  initializeState(): void {
    this.initialValue = this.initialValue ?? this.value ?? '';
    this._elementState = setInitialElementState(
      this.initialValue,
      this.controlName
    );
    this.updateValue();
  }

  /**
   * Stops masking delay timeout & resets masking state.
   */
  clearMasking(): void {
    clearTimeout(this._maskingDelay);
    this._maskingActive = false;
  }

  /**
   * Applies masking to Input Field after specified delay (on blur) OR
   * immediately (on first load).
   * @param immediate - skips "blurDelay" timeout if true
   */
  applyMasking(immediate = false): void {
    if (
      this._hasFocus ||
      !this.maskingConfig ||
      !hasValue(this._elementState?.value)
    )
      return;

    this.clearMasking();
    this._maskingDelay = setTimeout(
      () => {
        this._maskingActive = true;
      },
      immediate ? 0 : this.maskingConfig.blurDelay ?? 0
    );
  }

  /**
   * Gets string to be displayed within <input> element. Returns
   * masked value (if "maskingConfig" provided) or "_elementState.value".
   * If rounding is enabled for number Input Field & <input> currently
   * has :focus, returns current un-rounded value (prevents immediate
   * update that could be interpreted as a bug).
   */
  getInputDisplayValue(): string | number {
    if (!hasValue(this._elementState?.value)) return;

    switch (true) {
      case !this._updateDisplayValue &&
        this._hasFocus &&
        shouldApplyInlineFormatting(this.config, this.updateStrategy) &&
        this.type === FluidInputType.NUMBER:
        return String(this._elementRef?.value).replaceAll(',', '');

      case !this._updateDisplayValue && this._hasFocus:
        return this._elementRef?.value;

      case !!this.maskingConfig?.maskingFn && this._maskingActive:
        return this.maskingConfig.maskingFn(this._elementState.value);

      default:
        return this.parseValue(this._elementState.value);
    }
  }

  /**
   * Converts string <input> value to number (if "type" Prop is "number")
   * or returns original string value (for all other Input Field types).
   * 0 (false) will be converted to string '0'
   * @param value - current <input> value string
   */
  parseValue(value: string): number | string {
    if ((typeof value === 'number' && value === 0) || value === '0') return '0';

    return this.getInputType() === FluidInputType.NUMBER
      ? parseFloat(value as string)
      : value;
  }

  /**
   * Clears masking when <input> receives :focus.
   */
  handleFocus(): void {
    if (this.disabled || this.readonly) return;
    this._hasFocus = true;

    if (
      shouldApplyInlineFormatting(this.config, this.updateStrategy) &&
      this.type === FluidInputType.NUMBER
    ) {
      this._updateDisplayValue = false;
    }

    this.clearMasking();
  }

  /**
   * Validates <input> value & applies masking when component loses
   * :focus. If "type" Prop is "number" & rounding is enabled, also
   * causes rounded value to be displayed.
   */
  handleBlur(blurEvent: FocusEvent): void {
    if (this.disabled || this.readonly) return;
    this._hasFocus = false;
    this._updateDisplayValue = true;
    if (this.updateStrategy?.toUpperCase() === FluidUpdateStrategy.BLUR) {
      update(this, blurEvent, () => this.applyRounding());
    }
    this.markTouchedAndValidate().then(() => {
      this.applyMasking();
    });
  }

  /**
   * Updates internal state value when Input Field's value changes.
   * Also calls rounding function if "type" Prop is "number" &
   * rounding is enabled.
   * @param event - the InputEvent fired when <input> value changes
   */
  handleInput(event: InputEvent): void {
    event.preventDefault();
    if (
      this.disabled ||
      this.readonly ||
      this.updateStrategy?.toUpperCase() === FluidUpdateStrategy.BLUR
    )
      return;
    if (isDebouncedDelete(event, this.debounceDelete)) {
      update(
        this,
        event,
        () => this.applyRounding(),
        true,
        this._debouncedValueChangeEmitter(this)
      );
    } else {
      update(
        this,
        event,
        () => this.applyRounding(),
        true,
        () => emitValueChangeEvent(this)
      );
    }
  }

  /**
   * If "wholeOnly" Prop is true, rounds decimal number value to whole
   * number according to the specified "roundingMethod".
   */
  applyRounding() {
    this._elementState = handleNumberCoercion(
      this._elementState,
      this.type,
      this.outputType
    );
    if (!this.wholeOnly || this.getInputType() !== FluidInputType.NUMBER)
      return;
    if (!Utils.isEmpty(this._elementState.value)) {
      this._elementState.value = FluidFormatters.applyRounding(
        this._elementState.value,
        this.roundingMethod
      );
      this._updateDisplayValue = false; // Prevent rounded value from being displayed until input loses :focus
    }
    return this._elementState;
  }

  /**
   * Returns correct "type" for <input> element. If masking is applied
   * to a number input, returns "text" (number inputs don't support
   * non-numeric characters). If "type" Prop is "currency", returns
   * "number" ("currency" inputs don't technically exist).
   * @param forAttr - whether the return value will be used for the <input> element's "type" attribute
   */
  getInputType(forAttr?: boolean): FluidInputType {
    switch (true) {
      case !!forAttr && this._maskingActive:
        return FluidInputType.TEXT;

      case this.type === FluidInputType.CURRENCY:
        return FluidInputType.NUMBER;

      case !this._updateDisplayValue &&
        this._hasFocus &&
        shouldApplyInlineFormatting(this.config, this.updateStrategy) &&
        this.type === FluidInputType.NUMBER:
        return FluidInputType.NUMBER;

      case shouldApplyInlineFormatting(this.config, this.updateStrategy):
        return FluidInputType.TEXT;

      default:
        return this.type;
    }
  }

  /**
   * Determines whether "required" validation exists in the "validation"
   * Prop's array.
   */
  isRequired(): boolean {
    return !!this.validation?.some(
      (v) => v.type === FluidFieldValidationType.REQUIRED
    );
  }

  /**
   * Prevents "wheel" events (i.e. scrolling) from incrementing /
   * decrementing embedded <input type="number">. Also removes :focus
   * from the <input> to allow the page to continue scrolling.
   * @param event - WheelEvent occurring on embedded <input> element
   */
  preventNumberScroll(event: WheelEvent): void {
    if (this.getInputType() !== FluidInputType.NUMBER || !this._hasFocus)
      return;
    event.preventDefault();
    this._elementRef.blur();
  }

  onCancel() {
    this.reset(true);
    this.markTouchedAndValidate();
  }

  /**
   * Sets the element refernce to the local variable and adds any passive listeners required.
   * @param ref
   */
  setElementRef(ref: HTMLInputElement) {
    this._elementRef = ref;
    EventUtils.addListener('wheel', this._elementRef, (event: any) =>
      this.preventNumberScroll(event)
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <FormElementWrapper host={this}>
        <FormElementContainer
          host={this}
          onCancel={this.onCancel.bind(this)}
          adornmentConfig={this.adornmentConfig}
          enableParts={this._enableParts}>
          <input
            type={this.getInputType(true)}
            id={getFormInputId(this.config?.id, this.controlName)}
            name={this._elementState.name}
            maxLength={
              this.getInputType() !== FluidInputType.NUMBER
                ? this.maxLength
                : null
            }
            step={
              this.getInputType() === FluidInputType.NUMBER ||
              this.getInputType() === FluidInputType.TIME
                ? this.step
                : null
            }
            value={this.getInputDisplayValue()}
            placeholder={translateProperty.bind(this)('placeholder')}
            disabled={this.disabled}
            readOnly={this.readonly}
            ref={(el) => this.setElementRef(el)}
            onInput={(event: InputEvent) => this.handleInput(event)}
            onFocus={() => this.handleFocus()}
            onBlur={(event: FocusEvent) => this.handleBlur(event)}
            required={this._isRequired}
            class={{
              'input-field-input': true,
              'read-only': this.readonly,
              disabled: this.disabled,
              'hide-stepper-buttons': !this.displayStepperButtons,
            }}
          />
        </FormElementContainer>
      </FormElementWrapper>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  _debouncedValueChangeEmitter: Function;

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * Sets <input> element text alignment. Used for editable Table cells.
   * @internal
   */
  @Prop({ mutable: true }) textAlign: FluidAlignment = FluidAlignment.LEFT;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;

  /**
   *  configuration for the adornment
   * @internal
   */
  @Prop({ mutable: true }) adornmentConfig: FluidIconConfig;

  /**
   * Enables "part" attributes on certain elements. IMPORTANT: this
   * Prop should only be set to "true" when the component is being used
   * within another FLUID component.
   * @internal
   */
  @Prop({ mutable: true }) _enableParts: boolean;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    if (this.analyticsConfig) {
      this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(
        this.host
      )?.id;
    }
  }
}
